import React from "react";
import { toast } from "react-toastify";
import classes from "./record.module.scss";

/**
 * The one field grid for edit-mode sections (rules doc 2026-10-05): columns
 * auto-fill at 240px+, 16px between rows and 12px between columns, every field
 * filling its cell. Long text and toggle rows go in a `wide` FormGridItem.
 */
export const FormGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <div className={`${classes.formGrid} ${className ?? ""}`}>{children}</div>
);

/** One grid cell. `wide` spans the full row (textareas, toggle chips, card lists) under the short fields. */
export const FormGridItem: React.FC<{ wide?: boolean; children: React.ReactNode; className?: string }> = ({ wide, children, className }) => (
    <div className={`${classes.formGridItem} ${wide ? classes.formGridWide : ""} ${className ?? ""}`}>{children}</div>
);

/** A full row of labelled on/off chips — the edit-form replacement for loose checkboxes. */
export const ToggleChips: React.FC<{ children: React.ReactNode; label?: string }> = ({ children, label }) => (
    <div className={`${classes.formGridWide} ${classes.toggleChips}`} role="group" aria-label={label}>
        {children}
    </div>
);

/** A labelled on/off chip (aria-pressed), the same look as the contact cards' Primary / Reachable. */
export const ToggleChip: React.FC<{
    isOn: boolean;
    onToggle: () => void;
    title: string;
    disabled?: boolean;
    children: React.ReactNode;
}> = ({ isOn, onToggle, title, disabled, children }) => (
    <button
        type="button"
        className={`btn-unstyled ${classes.toggleChip} ${isOn ? classes.toggleChipOn : ""}`}
        aria-pressed={isOn}
        title={title}
        disabled={disabled}
        onClick={onToggle}
    >
        <i className={`bi ${isOn ? "bi-check2" : "bi-plus"}`} aria-hidden="true" />
        {children}
    </button>
);

/**
 * A read-only value in the grid, boxed like a disabled field with its notched
 * label: references (mono + copy) or a link to a related record.
 */
export const ReadOnlyField: React.FC<{
    label: string;
    value: React.ReactNode;
    /** Monospace (references, codes). */
    mono?: boolean;
    /** Text the copy icon copies. */
    copy?: string;
    tooltip?: string;
}> = ({ label, value, mono, copy, tooltip }) => (
    <div className={`crx-field crx-field--disabled ${classes.readOnlyField}`} title={tooltip}>
        <span className="crx-field-label">{label}</span>
        <span className={`${classes.readOnlyValue} ${mono ? classes.readOnlyMono : ""}`}>{value}</span>
        {copy && (
            <button
                type="button"
                className={`btn-unstyled ${classes.selectIcon}`}
                title={`Copy ${label.toLowerCase()}`}
                aria-label={`Copy ${label.toLowerCase()}`}
                onClick={async () => {
                    try {
                        await navigator.clipboard.writeText(copy);
                        toast.success("Copied to clipboard");
                    } catch {
                        toast.error("Could not copy — select the text and press Ctrl+C");
                    }
                }}
            >
                <i className="bi bi-clipboard" aria-hidden="true" />
            </button>
        )}
    </div>
);

/** Identity & status body: the photo tile on the left, the fields beside it (stacked on phones). */
export const IdentityBody: React.FC<{ photo: React.ReactNode; children: React.ReactNode }> = ({ photo, children }) => (
    <div className={classes.identityBody}>
        {photo}
        <div className={classes.formGridItem}>{children}</div>
    </div>
);

/** The edit form's stack of section Panels (16px apart). */
export const EditPanels: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className={classes.editPanels}>{children}</div>
);

export default FormGrid;
